import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Form, Input, Switch, Button, Space, Spin, Alert, message, Select } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getContact,
  createContact,
  updateContact,
  type CreateContactRequest,
  type UpdateContactRequest,
} from '@/api/contactApi';
import { listCustomers, type Customer } from '@/api/customerApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

interface ContactFormValues {
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  department?: string;
  customer_id: string;
  is_primary?: boolean;
  notes?: string;
  tags?: string[];
}

interface CustomerOption {
  value: string;
  label: string;
}

export default function ContactForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<ContactFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);

  const prefilledCustomerId = searchParams.get('customer_id') ?? '';
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  const searchCustomers = useCallback((keyword: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const res = await listCustomers({ from: 0, size: 20, keyword: keyword || undefined });
        setCustomerOptions(
          res.items.map((c: Customer) => ({ value: c.id, label: `${c.name}${c.company ? ` (${c.company})` : ''}` }))
        );
      } catch {
        setCustomerOptions([]);
      } finally {
        setCustomerSearching(false);
      }
    }, 300);
  }, []);

  // Load initial customer options (for prefilled or edit mode)
  useEffect(() => {
    searchCustomers('');
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchCustomers]);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getContact(id)
      .then((contact) => {
        form.setFieldsValue({
          name: contact.name,
          email: contact.email || undefined,
          phone: contact.phone || undefined,
          title: contact.title || undefined,
          department: contact.department || undefined,
          customer_id: contact.customer_id,
          is_primary: contact.is_primary,
          notes: contact.notes || undefined,
          tags: (contact.tags?.length ?? 0) > 0 ? contact.tags : undefined,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: ContactFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const data: UpdateContactRequest = {
          name: values.name,
          email: values.email || undefined,
          phone: values.phone || undefined,
          title: values.title || undefined,
          department: values.department || undefined,
          customer_id: values.customer_id,
          is_primary: values.is_primary,
          notes: values.notes || undefined,
          tags: values.tags || undefined,
        };
        await updateContact(id, data);
        message.success(t('contacts.update_success'));
        navigate(`/contacts/${id}`);
      } else {
        const data: CreateContactRequest = {
          name: values.name,
          email: values.email || undefined,
          phone: values.phone || undefined,
          title: values.title || undefined,
          department: values.department || undefined,
          customer_id: values.customer_id,
          is_primary: values.is_primary,
          notes: values.notes || undefined,
          tags: values.tags || undefined,
        };
        const created = await createContact(data);
        message.success(t('contacts.create_success'));
        navigate(`/contacts/${created.id}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('app.error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('contacts', 'write')) {
    return <Navigate to="/contacts" replace />;
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(isEdit ? `/contacts/${id}` : '/contacts')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('contacts.edit_title') : t('contacts.create_title')}</h2>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ is_primary: false, customer_id: prefilledCustomerId }}
        style={{ maxWidth: 800 }}
      >
        <Form.Item
          name="name"
          label={t('contacts.field.name')}
          rules={[
            { required: true, message: t('contacts.validation.name_required') },
            { max: 200, message: t('contacts.validation.name_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label={t('contacts.field.email')}
          rules={[
            { type: 'email', message: t('contacts.validation.email_invalid') },
            { max: 200, message: t('contacts.validation.email_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="phone"
          label={t('contacts.field.phone')}
          rules={[{ max: 50, message: t('contacts.validation.phone_max_length') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="title"
          label={t('contacts.field.title')}
          rules={[{ max: 200, message: t('contacts.validation.title_max_length') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="department"
          label={t('contacts.field.department')}
          rules={[{ max: 100, message: t('contacts.validation.department_max_length') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="customer_id"
          label={t('contacts.field.customer_id')}
          rules={[{ required: true, message: t('contacts.validation.customer_id_required') }]}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={searchCustomers}
            loading={customerSearching}
            options={customerOptions}
            placeholder={t('contacts.select_customer')}
          />
        </Form.Item>

        <Form.Item name="is_primary" label={t('contacts.field.is_primary')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('contacts.field.notes')}
          rules={[{ max: 2000, message: t('contacts.validation.notes_max_length') }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item name="tags" label={t('contacts.field.tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(isEdit ? `/contacts/${id}` : '/contacts')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
